import React from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@material-ui/core/Typography';

import { ExternalProductsData } from 'pages/Accounts/Edit/components/ExternalProducts/model';

import useOverviewStyles from 'pages/Accounts/Detail/Overview/overviewStyles';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { formatAmountValue } from 'helpers/helpers';
import { formatAxisAmmountValue } from 'helpers/chart';
import { Currency, EdwardColors } from 'enums';
import makeStyles from '@material-ui/core/styles/makeStyles';
import createStyles from '@material-ui/core/styles/createStyles';

const useStyles = makeStyles(() =>
  createStyles({
    fixChart: {
      maxWidth: '1024px',
      margin: '16px auto 0 auto',
      '& .highcharts-tooltip path': {
        display: 'none',
      },
      /*
      //TODO: needs a better selector, not sure if it's even possible via CSS
      //TODO: maybe use https://www.npmjs.com/package/highcharts-border-radius
      '& .highcharts-series-0 rect': {
        clipPath: 'xywh(0 0px 100% 100% round 10px 10px 0 0)'
      },
      */
      '& ._tooltip': {
        zIndex: 1000,
        '& > span': {
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '8px',
          display: 'inline-block',
          fontSize: '0.8em',
          textAlign: 'center',
          boxSizing: 'border-box',
          borderRadius: '10px',
          border: 'solid 2px #000',
        },
      },
    },
  }),
);

type StackingOptions = undefined | 'normal' | 'percent';

export interface BarData {
  name: string;
  data: number[];
  stacking?: StackingOptions;
  color?: string;
}

interface Props {
  data: ExternalProductsData;
  currency: Currency;
  overview?: boolean;
  original?: boolean;
  pdf?: boolean;
}

const Balance: React.FC<Props> = ({ data, overview, original, pdf, currency }) => {
  const { t } = useTranslation();
  const overviewClasses = useOverviewStyles();
  const classes = useStyles();

  const formatData = () => {
    if (!data.balanceSheet.dataset) return [];
    return (
      Object.entries(data.balanceSheet.dataset)
        /*
      .sort(([key, value], [keyB, valueB]) => {
        const extProdData = data.externalProducts.find((p) => p.id === key);
        return extProdData?.category === ExtProductCategory.LOAN ? -1 : 0;
      })
      */
        .map(([key, value]) => {
          const extProdData = data.externalProducts.find((p) => p.id === key);
          const finInst = extProdData?.financialInstitution ? extProdData.financialInstitution.title : undefined;
          return {
            name: extProdData
              ? `${finInst ? finInst + '<br>(' : ''}${extProdData.title}${finInst ? ')' : ''}`
              : t(`accountPlan_balance_${key}`),
            stacking: 'normal',
            data: value,
          };
        })
    );
  };

  const formattedData = formatData();

  const defaultSettings: Highcharts.Options = {
    title: {
      text: '',
    },
    credits: {
      enabled: false,
    },
    chart: {
      type: 'column',
      animation: !pdf,
    },
    colors: ['#000', EdwardColors.GRAY_MEDIUM, EdwardColors.DARK],

    tooltip: {
      borderRadius: 20,
      borderWidth: 2,
      shadow: false,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      useHTML: true,
      className: '_tooltip',
      headerFormat: '',
      pointFormatter: function () {
        return `${this.series.name}<br /><b>${formatAmountValue(
          this.y!,
          t('currency_' + currency),
        )}</b><br/>${Math.round(this.percentage!)} %`;
      },
    },
    plotOptions: {
      series: {
        animation: !pdf,
      },
      column: {
        colorByPoint: true,
        borderColor: 'rgba(255,255,255,0.25)',
        pointWidth: 100,
        inactiveOtherPoints: true,
      } as Highcharts.PlotColumnOptions & { inactiveOtherPoints: boolean },
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      categories: [t('accountPlan_balance_total'), t('accountPlan_balance_loans'), t('accountPlan_balance_property')],
      labels: {
        formatter: function (this) {
          return `<b>${this.value}</b><br /> (${formatAxisAmmountValue(
            data.balanceSheet.summary[this.pos],
            t(`currency_${currency}`),
          )})`;
        },
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
          return formatAxisAmmountValue(this.value, t(`currency_${currency}`));
        },
      },
    },

    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 600,
          },
          chartOptions: {
            yAxis: {
              labels: {
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject<number>) {
                  return formatAmountValue(this.value, currency, false, true);
                },
              },
            },
            plotOptions: {
              column: {
                pointWidth: 80,
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 400,
          },
          chartOptions: {
            plotOptions: {
              column: {
                pointWidth: 50,
              },
            },
          },
        },
      ],
    },

    series: formattedData as any,
  };

  return (
    <>
      {overview && (
        <Typography
          variant="h3"
          className={`${
            original ? overviewClasses.originalSubtitle : overview ? overviewClasses.proposedSubtitle : ''
          }`}
        >
          {t('externalProducts_balance')}
        </Typography>
      )}

      <div className={classes.fixChart}>
        <HighchartsReact highcharts={Highcharts} options={defaultSettings as Highcharts.Options} />
      </div>
    </>
  );
};

export default React.memo(Balance);
